import { useEffect, useState } from "react";
import { api } from "../../api";
import { fmtDate } from "../../lib/format";
import { ThumbnailUploader } from "../../components/ThumbnailUploader";
import { RichTextEditor } from "../../components/RichTextEditor";
import {
  SectionHeading,
  StatusBadge,
  ErrorBanner,
  SuccessBanner,
} from "../../components/ui";

interface Row {
  id: string;
  title: string;
  status: string;
  publishedAt: string | null;
  pinnedUntil: string | null;
}

type Status = "draft" | "published" | "archived";

const JST_OFFSET_MS = 9 * 3600000;
// サーバ返却値 (ISO / naive UTC) を datetime-local "YYYY-MM-DDTHH:mm" に整形 (JST 固定)
function toDatetimeLocal(v: string | null | undefined): string {
  if (!v) return "";
  const s = String(v).trim();
  const hasTz = /[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  const d = new Date(hasTz ? s : s.replace(" ", "T") + "Z");
  if (isNaN(d.getTime())) return "";
  const jst = new Date(d.getTime() + JST_OFFSET_MS);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${jst.getUTCFullYear()}-${pad(jst.getUTCMonth() + 1)}-${pad(jst.getUTCDate())}T${pad(jst.getUTCHours())}:${pad(jst.getUTCMinutes())}`;
}
// datetime-local (ブラウザTZ=JST 想定) → ISO (UTC)
function datetimeLocalToIso(v: string): string | null {
  if (!v) return null;
  const ms = new Date(v).getTime();
  if (isNaN(ms)) return null;
  return new Date(ms).toISOString();
}

const STATUS_LABELS: Record<Status, string> = {
  draft: "下書き",
  published: "公開",
  archived: "アーカイブ",
};
const STATUS_TONE: Record<
  Status,
  "success" | "warning" | "danger" | "neutral"
> = {
  draft: "neutral",
  published: "success",
  archived: "neutral",
};

interface Form {
  id: string | null; // null = 新規作成、string = 編集対象
  title: string;
  body: string;
  status: Status;
  thumbnailKey: string | null;
  notifyLine: boolean;
  publishedAt: string; // 配信予約 (datetime-local, 空=即時)
}

const INITIAL: Form = {
  id: null,
  title: "",
  body: "",
  status: "draft",
  thumbnailKey: null,
  notifyLine: true,
  publishedAt: "",
};

export function AdminNoticesPage() {
  const [items, setItems] = useState<Row[]>([]);
  const [form, setForm] = useState<Form>(INITIAL);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = () =>
    api.get<{ items: Row[] }>("/admin/notices").then((r) => setItems(r.items));
  useEffect(() => {
    load();
  }, []);

  const startEdit = async (id: string) => {
    setErr(null);
    setMsg(null);
    try {
      const d = await api.get<{
        id?: string;
        title: string;
        body: string;
        status: string;
        thumbnailKey?: string | null;
        publishedAt?: string | null;
      }>(`/admin/notices/${id}`);
      setForm({
        id: d.id ?? id,
        title: d.title,
        body: d.body,
        status: (d.status as Status) ?? "draft",
        thumbnailKey: d.thumbnailKey ?? null,
        notifyLine: true,
        publishedAt: toDatetimeLocal(d.publishedAt),
      });
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const cancelEdit = () => {
    setForm(INITIAL);
    setErr(null);
    setMsg(null);
  };

  const submit = async () => {
    setErr(null);
    setMsg(null);
    try {
      const publishedAtIso = form.publishedAt
        ? datetimeLocalToIso(form.publishedAt)
        : null;
      if (form.id) {
        await api.patch(`/admin/notices/${form.id}`, {
          title: form.title,
          body: form.body,
          status: form.status,
          thumbnailKey: form.thumbnailKey,
          notifyLine: form.notifyLine,
          publishedAt: publishedAtIso,
        });
        setMsg("更新しました");
      } else {
        await api.post("/admin/notices", {
          title: form.title,
          body: form.body,
          status: form.status,
          thumbnailKey: form.thumbnailKey,
          notifyLine: form.notifyLine,
          publishedAt: publishedAtIso,
        });
        setMsg("作成しました");
      }
      setForm(INITIAL);
      await load();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const isEditing = form.id !== null;

  return (
    <div className="space-y-4">
      <SectionHeading level={2}>お知らせ管理</SectionHeading>
      <div className="card p-4 md:p-5 space-y-2">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-sm">
            {isEditing ? "編集" : "新規作成"}
          </h3>
          {isEditing && (
            <button onClick={cancelEdit} className="text-xs underline text-tora-ink/70">
              キャンセル（新規作成に戻る）
            </button>
          )}
        </div>
        <div>
          <label className="label">タイトル</label>
          <input
            className="input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </div>
        <div>
          <label className="label">本文</label>
          <RichTextEditor
            value={form.body}
            onChange={(html) => setForm({ ...form, body: html })}
            placeholder="太字・見出し・リンク・画像など自由にレイアウトできます"
          />
        </div>
        <div>
          <label className="label">ステータス</label>
          <select
            className="input"
            value={form.status}
            onChange={(e) =>
              setForm({ ...form, status: e.target.value as Status })
            }
          >
            <option value="draft">下書き</option>
            <option value="published">公開</option>
            <option value="archived">アーカイブ</option>
          </select>
        </div>
        <div>
          <label className="label">公開日時（配信予約・任意）</label>
          <input
            type="datetime-local"
            className="input"
            value={form.publishedAt}
            onChange={(e) => setForm({ ...form, publishedAt: e.target.value })}
          />
          <p className="text-[11px] text-tora-ink/50 mt-1">
            未来の日時を指定し「公開」で保存すると、その時刻まで会員側に表示されず、
            時刻到来時に LINE 通知も送られます。空欄なら即時公開・即時通知です。
          </p>
        </div>
        <ThumbnailUploader
          value={form.thumbnailKey}
          onChange={(k) => setForm({ ...form, thumbnailKey: k })}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.notifyLine}
            onChange={(e) => setForm({ ...form, notifyLine: e.target.checked })}
          />
          公開時に LINE 連携会員へ通知する
        </label>
        <p className="text-[11px] text-tora-ink/50">
          「公開」で保存した時に、LINE 連携済みの会員へお知らせを 1 回だけ配信します。
          （下書き/アーカイブでは送信されません。既に通知済みのお知らせは再送されません）
        </p>
        {err && <ErrorBanner>{err}</ErrorBanner>}
        {msg && <SuccessBanner>{msg}</SuccessBanner>}
        <div className="flex flex-wrap items-center gap-2">
          <button className="btn-primary" onClick={submit}>
            {isEditing ? "更新" : "作成"}
          </button>
          {isEditing && form.id && (
            <a
              href={`/notices/${form.id}?preview=1`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline text-sm"
            >
              プレビュー（別タブ）
            </a>
          )}
        </div>
        {!isEditing && (
          <p className="text-[11px] text-tora-ink/50">
            プレビューは「下書き」で一度作成・保存すると、編集時に確認できます。
          </p>
        )}
      </div>

      <div className="overflow-x-auto">
      <table className="w-full text-sm border border-black/10">
        <thead className="bg-black/5 text-left">
          <tr>
            <th className="p-2">タイトル</th>
            <th className="p-2">ステータス</th>
            <th className="p-2">公開日</th>
            <th className="p-2">操作</th>
          </tr>
        </thead>
        <tbody>
          {items.map((n) => (
            <tr key={n.id} className="border-t border-black/10">
              <td className="p-2">{n.title}</td>
              <td className="p-2">
                <StatusBadge
                  tone={STATUS_TONE[n.status as Status] ?? "neutral"}
                >
                  {STATUS_LABELS[n.status as Status] ?? n.status}
                </StatusBadge>
              </td>
              <td className="p-2">{n.publishedAt ? fmtDate(n.publishedAt) : "-"}</td>
              <td className="p-2 flex items-center gap-2">
                <button
                  className="text-tora underline text-xs"
                  onClick={() => startEdit(n.id)}
                >
                  編集
                </button>
                <button
                  className="text-xs text-tora-ink/70 hover:text-tora"
                  onClick={() => {
                    const url = `${window.location.origin}/notices/${n.id}`;
                    navigator.clipboard?.writeText(url);
                  }}
                  title="会員向けリンクをコピー"
                >
                  🔗 リンク
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}
